import React, { useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { CheckedInUser } from "src/components/CheckedIn/checkedInUser";
import { CheckedInAction } from "src/components/CheckedIn/CheckedInSiteStyles";
import { CheckedInIcon } from "src/components/CheckedIn/CheckedInIcon";
import { SearchBox, ViewerBar, ViewerMessage, ViewerRoot, ViewerSection } from "src/components/CheckedIn/CheckedInContentStyles";
import { getNotice, listFeedNotices } from "../api/feedApi";
import { mergeNoticeFeedUpdate } from "../feedUtils";
import { Notice } from "./noticeTypes";
import { NoticePost } from "./NoticePost";
import { NoticeList } from "./NoticeViewerStyles";

export default function NoticeboardViewer({ user }: { user: CheckedInUser }): React.ReactElement {
    const history = useHistory();
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const noticeUuid: string | null = params.get("notice");
    const search: string = params.get("search") || "";
    const [query, setQuery] = useState<string>(search);
    const [notices, setNotices] = useState<Notice[]>([]);
    const [post, setPost] = useState<Notice | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);
    const [feedLoading, setFeedLoading] = useState<boolean>(true);
    const [feedError, setFeedError] = useState<boolean>(false);
    const [retry, setRetry] = useState<number>(0);
    const [visibleCount, setVisibleCount] = useState<number>(10);
    const listOffset = useRef<number>(0);

    useEffect(() => { setQuery(search); }, [search]);
    useEffect(() => {
        if (query === search || noticeUuid) {
            return;
        }
        const timer = window.setTimeout(() => {
            const next = new URLSearchParams();
            if (query.trim()) {
                next.set("search", query.trim());
            }
            history.replace({ pathname: "/noticeboard", search: next.toString() });
        }, 300);
        return () => window.clearTimeout(timer);
    }, [query, search, noticeUuid, history]);

    useEffect(() => {
        let active = true;
        setFeedLoading(true);
        setFeedError(false);
        setVisibleCount(10);
        void listFeedNotices(user.token, null, { search }).then((data: Notice[]) => {
            if (active) {
                setNotices(Array.from(new Map(data.map(notice => [notice.uuid, notice])).values())
                    .sort((a, b) => new Date(b.postedDate).getTime() - new Date(a.postedDate).getTime()));
                setFeedLoading(false);
            }
        }).catch(() => {
            if (active) {
                setFeedError(true);
                setFeedLoading(false);
            }
        });
        return () => { active = false; };
    }, [user.token, search, retry]);

    useEffect(() => {
        let active = true;
        if (!noticeUuid) {
            setPost(null);
            window.scrollTo({ top: listOffset.current });
            return;
        }
        window.scrollTo({ top: 0 });
        setPost(null);
        setLoading(true);
        setError(false);
        void getNotice(user.token, noticeUuid).then((data: Notice) => {
            if (active) {
                setPost(data);
                setLoading(false);
            }
        }).catch(() => {
            if (active) {
                setError(true);
                setLoading(false);
            }
        });
        return () => { active = false; };
    }, [noticeUuid, user.token, retry]);

    const updated = (notice: Notice): void => {
        setNotices(current => current.map(item => item.uuid === notice.uuid ? mergeNoticeFeedUpdate(item, notice) : item));
        setPost(current => current?.uuid === notice.uuid ? mergeNoticeFeedUpdate(current, notice) : current);
    };
    const close = (): void => {
        const next = new URLSearchParams(location.search);
        next.delete("notice");
        history.push({ pathname: "/noticeboard", search: next.toString() });
    };
    const open = (notice: Notice): void => {
        listOffset.current = window.scrollY;
        const next = new URLSearchParams(location.search);
        next.set("notice", notice.uuid);
        history.push({ pathname: "/noticeboard", search: next.toString() });
    };
    const deleted = (uuid: string): void => {
        setNotices(current => current.filter(notice => notice.uuid !== uuid));
        close();
    };
    const pending: boolean = noticeUuid ? loading : feedLoading;
    const failed: boolean = noticeUuid ? error : feedError;
    return <ViewerRoot>
        {noticeUuid ? <ViewerBar><span /><CheckedInAction type="button" $round aria-label="Close post" onClick={close}>
            <CheckedInIcon name="close" /></CheckedInAction></ViewerBar> : <SearchBox>
            <input type="search" aria-label="Search noticeboard" placeholder="Search noticeboard" value={query}
                onChange={event => setQuery(event.target.value)} />
            <CheckedInIcon name="search" />
        </SearchBox>}
        {pending ? <ViewerMessage role="status">Loading posts…</ViewerMessage> : failed ? <ViewerMessage $error role="alert">
            We couldn’t load {noticeUuid ? "this post" : "the Noticeboard"}. Please try again.
            <div><CheckedInAction type="button" onClick={() => setRetry(value => value + 1)}>Try again</CheckedInAction></div>
        </ViewerMessage> : noticeUuid ? post && <NoticePost key={post.uuid} notice={post} user={user} detail
            onOpen={() => undefined} onUpdated={updated} onDeleted={deleted} /> : <ViewerSection>
            <NoticeList>
                {notices.slice(0, visibleCount).map(notice => <NoticePost key={notice.uuid} notice={notice} user={user}
                    detail={false} onOpen={() => open(notice)} onUpdated={updated} onDeleted={deleted} />)}
            </NoticeList>
            {notices.length === 0 && <ViewerMessage>{search ? "No posts match your search." : "No published posts yet."}</ViewerMessage>}
            {visibleCount < notices.length && <CheckedInAction type="button" $monochrome
                onClick={() => setVisibleCount(value => value + 10)}>Load more posts</CheckedInAction>}
        </ViewerSection>}
    </ViewerRoot>;
}
